import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/forms/Buttons/Button";
import { TrashIcon, EyeIcon } from "@heroicons/react/24/outline";
import Select from "../../../components/forms/Select/Select";
import { patchTeamPassed } from "../../../services/olympiad/teamPassedService";
import { toast } from "react-toastify";

export const useTeamColumns = (
  handleDelete,
  handleView,
  isExamCenterAdmin,
  update,
  pageSize
) => {
  const [t] = useTranslation();

  return useMemo(
    () => [
      {
        Header: t("admin.team_name"),
        accessor: "name",
      },
      {
        Header: t("admin.region"),
        accessor: "region",
        Cell: ({ value }) => value?.name || "-",
      },

      ...[1, 2, 3].map((member) => ({
        Header: t(`admin.team_member_result`, { member }),
        accessor: `team_members`,
        id: `team_members_${member}`,
        Cell: ({ value }) => {
          const result = value?.filter(
            (member) => member.role === "PARTICIPANT"
          )?.[member - 1]?.user?.user_results?.[0];
          const total = result?.total_correct + result?.total_wrong;
          return (
            <div className={`flex flex-row items-center justify-center gap-2 `}>
              <span
                className={`${
                  result?.result >= total * 0.375
                    ? ""
                    : "font-semibold text-red-500"
                } `}
              >
                {result?.result || "0"}
              </span>
            </div>
          );
        },
      })),

      {
        Header: t("admin.team_total_result"),
        accessor: "total_results",
        accessor_for_ordering: "total_results",
        Cell: ({ value }) => {
          return (
            <div className="flex flex-row items-center justify-center gap-2 font-bold">
              <span className="">{value}</span>
            </div>
          );
        },
      },
      {
        Header: t("admin.status"),
        accessor: "team_passed",
        Cell: ({ value, row }) => {
          const level_passed = value?.[0];
          return (
            <div className="text-center z-50">
              <Select
                menuPlacement={row.index >= pageSize - 2 ? "top" : "bottom"}
                options={[
                  {
                    label: t("admin.qualified"),
                    value: true,
                    backgroundColor: "#22c55e",
                    color: "#ffffff",
                  },
                  {
                    label: t("admin.unqualified"),
                    value: false,
                    backgroundColor: "#ef4444",
                    color: "#ffffff",
                  },
                ]}
                value={level_passed?.is_passed}
                onChange={(opt) => {
                  console.log(opt);
                  update(row.original.uuid, {
                    team_passed: value.map((passed) =>
                      passed.uuid === level_passed?.uuid
                        ? {
                            ...passed,
                            is_passed: opt.value,
                            type: opt.value === false ? null : passed.type,
                          }
                        : passed
                    ),
                  });
                  const body = {
                    is_passed: opt.value,
                  };
                  if (opt.value === false) {
                    body.type = null;
                  }
                  patchTeamPassed(level_passed.uuid, JSON.stringify(body))
                    .then(() => {
                      toast.success(t("admin.team_passed_updated"));
                    })
                    .catch((err) => {
                      toast.error(
                        err.response?.data?.detail ||
                          t("admin.error_updating_team_passed")
                      );
                    });
                }}
              />
            </div>
          );
        },
      },
      {
        Header: `${t("admin.status")} `,
        id: "team_passed__type",
        Cell: ({ value, row }) => {
          const level_passed = row.original?.team_passed?.[0];
          if (!level_passed?.is_passed) return <span></span>;
          return (
            <Select
              menuPlacement={row.index >= pageSize - 2 ? "top" : "bottom"}
              options={[
                {
                  label: 1,
                  value: 0,
                  backgroundColor: "#22c55e",
                  color: "#ffffff",
                },
                {
                  label: 2,
                  value: 1,
                  backgroundColor: "#6351fd",
                  color: "#ffffff",
                },
              ]}
              value={level_passed?.type}
              onChange={(opt) => {
                update(row.original.uuid, {
                  team_passed: row.original?.team_passed?.map((passed, i) =>
                    passed.uuid === level_passed?.uuid
                      ? {
                          ...passed,
                          type: opt.value,
                        }
                      : passed
                  ),
                });
                patchTeamPassed(level_passed.uuid, { type: opt.value })
                  .then(() => {
                    toast.success(t("admin.team_passed_updated"));
                  })
                  .catch((err) => {
                    toast.error(
                      err.response?.data?.detail ||
                        t("admin.error_updating_team_passed")
                    );
                  });
              }}
            />
          );
        },
      },
      {
        Header: t("admin.actions"),
        accessor: "uuid",
        Cell: ({ row }) => (
          <div className="flex gap-2">
            <Button
              Icon={EyeIcon}
              onClick={() => handleView(row.original)}
              color="primary"
            />
          </div>
        ),
      },
    ],
    [t, handleView, pageSize, update]
  );
};
